(function () {
  'use strict';

  // Bail out cleanly if window is unavailable (SSR / non-browser).
  if (typeof window === 'undefined') return;

  // ---------------------------------------------------------------------------
  // Whitelist: TIB-owned keys without a leading "_"
  // Add new keys here if you need to preserve them without prefixing.
  // Keys that TIB already emits with "_" are auto-preserved by
  // prefixPropertyKeys(); listing them here is purely documentary and also
  // guards against malformed upstream data.
  // ---------------------------------------------------------------------------
  var TIB_OWNED_NON_UNDERSCORE_KEYS = {
    customization_id: true,      
    customization_image: true,   
    tib_design: true,            
    customization_edit_link: true
  };

  // ===========================================================================
  // HELPERS
  // ===========================================================================

  /**
   * Add a leading "_" to every key that does not already start with "_".
   * Returns a NEW object — the input is never mutated.
   *
   * Rules:
   *   - Key already starts with "_"                         -> kept as-is.
   *   - Key is in TIB_OWNED_NON_UNDERSCORE_KEYS whitelist  -> kept as-is.
   *   - Empty / non-string keys                            -> kept as-is.
   *   - Everything else                                     -> prefixed with "_".
   *
   * O(n) in number of properties; no nested iteration, no allocations
   * outside the result object.
   *
   * @param {Record<string, any> | undefined | null} properties
   * @returns {Record<string, any>}
   */
  function prefixPropertyKeys(properties) {
    if (!properties || typeof properties !== 'object') return {};

    var out = {};
    var keys = Object.keys(properties);
    // Fast-path: zero allocation if there is nothing to transform.
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      // Already prefixed or whitelisted? Keep as-is.
      if (key.charCodeAt(0) === 95 /* "_" */ || TIB_OWNED_NON_UNDERSCORE_KEYS[key]) {
        out[key] = properties[key];
      } else {
        out['_' + key] = properties[key];
      }
    }
    return out;
  }

  /**
   * Apply prefix to a single cart input item.
   * Returns the same item shape (id, quantity, properties) untouched
   * except for `properties`. The input object is NOT mutated.
   *
   * @param {{ id: string|number, quantity: number, properties?: Record<string, any> }} item
   * @returns {{ id: string|number, quantity: number, properties?: Record<string, any> }}
   */
  function prefixSingleItem(item) {
    if (!item || typeof item !== 'object') return item;
    return {
      id: item.id,
      quantity: item.quantity,
      properties: prefixPropertyKeys(item.properties)
    };
  }

  /**
   * Wraps an existing transformBeforeAddToCart hook so that ALL items in its
   * returned payload get property keys prefixed with "_".
   *
   * Supports all three return shapes from the SDK:
   *   - SingleItemCartInput:       { id, quantity, properties }
   *   - ListItemsCartInput:        [item, item, ...]
   *   - MultipleItemsCartInput:    { items: [item, item, ...] }
   * And both sync + async (Promise) previous hooks.
   *
   * Important: errors from the previous hook are propagated unchanged so
   * ATC failure paths (validation, network) still surface to the caller.
   *
   * @param {Function | undefined} previousHook
   * @returns {Function}
   */
  function buildPrefixHook(previousHook) {
    return function pawfecthousePrefixHook(input) {
      var prevResult;
      try {
        prevResult = typeof previousHook === 'function'
          ? previousHook.call(this, input)
          : input;
      } catch (err) {
        throw err;
      }

      // Async previous hook: chain prefix application onto its Promise.
      if (prevResult && typeof prevResult.then === 'function') {
        return prevResult.then(function (resolved) {
          return applyPrefixToResult(resolved);
        });
      }

      // Sync previous hook: apply immediately.
      return applyPrefixToResult(prevResult);
    };
  }

  /**
   * Detect the return shape and prefix properties accordingly.
   * If the shape is unrecognised, the value is returned untouched so we
   * never alter behaviour the SDK does not expect.
   */
  function applyPrefixToResult(result) {
    if (!result || typeof result !== 'object') return result;

    // MultipleItemsCartInput: { items: [...] }
    if (Array.isArray(result.items)) {
      return {
        items: result.items.map(prefixSingleItem)
      };
    }

    // ListItemsCartInput: [...]
    if (Array.isArray(result)) {
      return result.map(prefixSingleItem);
    }

    // SingleItemCartInput: { id, quantity, properties }
    // Use a stricter check than `in` to avoid false positives (e.g. a
    // configuration object that happens to expose `id` and `quantity`).
    if (Object.prototype.hasOwnProperty.call(result, 'id') && Object.prototype.hasOwnProperty.call(result, 'quantity')) {
      return prefixSingleItem(result);
    }

    return result;
  }

  // ===========================================================================
  // SAFE BINDING
  // ===========================================================================

  /**
   * Sentinel property attached to every wrapper we install. Lets us detect
   * "already wrapped" so repeated event fires don't stack nested wrappers.
   * Uses a unique, non-enumerable property to avoid leaking into dev tools.
   */
  var UNWRAP_TAG = '__pawfecthouseUnderscoreHook_v1__';

  /**
   * Install our wrapper onto `window.teeinblue.transformBeforeAddToCart`.
   * Tries in-place assignment first; falls back to Object.defineProperty
   * only if needed. Both paths set `configurable: true` so future overrides
   * (theme re-binding) keep working.
   */
  function installHook(previousHook) {
    var newHook = buildPrefixHook(previousHook);
    // Tag the wrapper so tryApplyHook() can detect re-entry.
    try {
      Object.defineProperty(newHook, UNWRAP_TAG, {
        value: true,
        enumerable: false,
        configurable: true,
        writable: false
      });
    } catch (tagErr) {
      // Tagging is best-effort; fall through to install anyway.
    }

    try {
      window.teeinblue.transformBeforeAddToCart = newHook;
    } catch (err) {
      try {
        Object.defineProperty(window.teeinblue, 'transformBeforeAddToCart', {
          configurable: true,
          enumerable: true,
          writable: true,
          value: newHook
        });
      } catch (err2) {
        return false;
      }
    }
    return true;
  }

  // ===========================================================================
  // EVENT-DRIVEN INIT
  // ===========================================================================

  var TIB_EVENT = 'teeinblue-event-component-injected';
  var THEME_REBIND_POLL_MS = 500;
  var THEME_REBIND_WINDOW_MS = 5000; // 5s — theme usually rebinds within 1s.

  function tryApplyHook() {
    if (!window.teeinblue || typeof window.teeinblue !== 'object') return false;

    var existing = window.teeinblue.transformBeforeAddToCart;

    if (existing && existing[UNWRAP_TAG] === true) {
      return true;
    }

    if (!installHook(existing)) return false;

    window._pawfecthouseUnderscoreHookApplied = true;

    setTimeout(observeThemeHookOverride, 0);
    return true;
  }

  /**
   * The Pawfecthouse theme binds its upsell hook inside the same
   * `teeinblue-event-component-injected` listener. Listener invocation order
   * is the order of `addEventListener` calls; we cannot guarantee ours runs
   * after the theme's. If the theme overwrites our wrapper, we re-wrap it.
   *
   * Polling is bounded by THEME_REBIND_WINDOW_MS to guarantee no ongoing
   * resource use after the page has finished initialising.
   */
  function observeThemeHookOverride() {
    if (!window.teeinblue) return;
    var lastSeen = window.teeinblue.transformBeforeAddToCart;

    var interval = setInterval(function () {
      if (!window.teeinblue) {
        clearInterval(interval);
        return;
      }
      var current = window.teeinblue.transformBeforeAddToCart;
      if (current && current !== lastSeen && current[UNWRAP_TAG] !== true) {
        if (installHook(current)) {
          lastSeen = window.teeinblue.transformBeforeAddToCart;
        }
      }
    }, THEME_REBIND_POLL_MS);

    setTimeout(function () { clearInterval(interval); }, THEME_REBIND_WINDOW_MS);
  }

  /**
   * Bootstrap. Listens for the SDK component-injected event and installs
   * the hook at that point.
   */
  function init() {
    if (window._pawfecthouseUnderscoreHookApplied) return;

    document.addEventListener(TIB_EVENT, function onInjected() {
      document.removeEventListener(TIB_EVENT, onInjected);
      tryApplyHook();
    });

    // Fallback: in case the event already fired before this script loaded
    if (window.teeinblue && typeof window.teeinblue === 'object') {
      tryApplyHook();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();